import { useState } from 'react';
import { Bell, Clock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { toast } from 'sonner';

interface ScriptReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scriptId: string;
  scriptTitle: string;
}

export const ScriptReminderDialog = ({
  open,
  onOpenChange,
  scriptId,
  scriptTitle,
}: ScriptReminderDialogProps) => {
  const { t } = useTranslation();
  const { isEnabled, requestPermission, scheduleReminder } = usePushNotifications();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');

  const handleSetReminder = async () => {
    if (!date || !time) {
      toast.error(t('notifications.reminder.selectDateTime'));
      return;
    }

    // Check if notifications are enabled, request if not
    if (!isEnabled) {
      const granted = await requestPermission();
      if (!granted) {
        toast.error(t('notifications.denied'));
        return;
      }
    }

    const reminderDate = new Date(`${date}T${time}`);
    
    if (reminderDate.getTime() <= Date.now()) {
      toast.error(t('notifications.reminder.futureTime'));
      return;
    }

    scheduleReminder(scriptTitle, reminderDate, scriptId);
    toast.success(t('notifications.reminder.set'));
    onOpenChange(false);
    setDate('');
    setTime('');
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#F5F0E8] border-2 border-black shadow-[8px_8px_0_#1A1A1A] gap-0 p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            {t('notifications.reminder.title')}
          </DialogTitle>
          <DialogDescription>
            {t('notifications.reminder.description', { title: scriptTitle })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reminder-date">{t('notifications.reminder.date')}</Label>
            <Input
              id="reminder-date"
              type="date"
              min={today}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reminder-time">{t('notifications.reminder.time')}</Label>
            <Input
              id="reminder-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              <X className="mr-2 h-4 w-4" />
              {t('notifications.reminder.cancel')}
            </Button>
            <Button onClick={handleSetReminder} className="flex-1">
              <Clock className="mr-2 h-4 w-4" />
              {t('notifications.reminder.set')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
